/**
 * 카탈로그 → 화면 구조.
 *
 * 서버가 준 배열을 분류(`categoryCode`)별로 묶는다. **거르지 않는다** — DRAFT 는 서버가
 * 이미 뺐고, 프론트에서 `priceStatus` 로 다시 필터링하면 규칙이 두 군데가 된다
 * (`catalog.ts` 의 고객 API 주석).
 *
 * 지금 응답은 계약(CONT) 8종뿐이라 그룹이 하나로 나오는데, 그건 버그가 아니라
 * 나머지 46종이 가격 미확정(DRAFT)이어서다. 운영에서 확정되는 대로 **배포 없이**
 * 그룹과 행이 늘어난다 — 그래서 분류 목록을 코드에 못박지 않고 응답에서 만든다.
 */

import type { CatalogProduct } from '@/lib/api/catalog';

export interface CatalogGroup {
  categoryCode: string;
  /** 표에 그대로 찍히는 분류 이름. 서버가 준 `categoryLabel` 이다 */
  heading: string;
  products: CatalogProduct[];
}

/**
 * 분류별로 묶는다. **그룹 순서도 상품 순서도 서버가 준 순서 그대로다.**
 *
 * 이름순·금액순으로 다시 정렬하지 않는다. 서버는 `sortOrder` 로 정렬해 내려주고
 * 그 순서에는 운영자가 위/아래 버튼으로 조정한 의도가 담겨 있다. 화면이 다시 정렬하면
 * 그 조정이 화면에 안 나타나 "옮겼는데 안 바뀐다"가 된다.
 */
export function groupByCategory(products: CatalogProduct[]): CatalogGroup[] {
  const groups: CatalogGroup[] = [];
  const byCode = new Map<string, CatalogGroup>();

  for (const product of products) {
    const existing = byCode.get(product.categoryCode);
    if (existing) {
      existing.products.push(product);
      continue;
    }
    const group: CatalogGroup = {
      categoryCode: product.categoryCode,
      heading: product.categoryLabel,
      products: [product],
    };
    byCode.set(product.categoryCode, group);
    groups.push(group);
  }

  return groups;
}

/** 코드로 한 건 찾기. 챗봇이 "이 상품 얼마예요"에 답할 때 쓴다. */
export function findByCode(products: CatalogProduct[], code: string): CatalogProduct | undefined {
  return products.find(p => p.code === code);
}

/**
 * 상품명·코드 부분일치 검색. 챗봇이 자연어에서 상품을 집을 때 쓴다.
 *
 * 공백을 지우고 대소문자를 무시한 뒤 비교한다 — 사람은 "계약서검토"라고 붙여 쓰고
 * 카탈로그에는 "계약서 검토 · 국문 표준 (10p 이하)"로 들어 있다.
 */
export function searchProducts(products: CatalogProduct[], keyword: string): CatalogProduct[] {
  const needle = keyword.replace(/\s+/g, '').toLowerCase();
  if (!needle) return [];
  return products.filter(p => `${p.code}${p.nameKo}`.replace(/\s+/g, '').toLowerCase().includes(needle));
}
